import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTimer } from '../../context/TimerContext';
import { useAuth } from '../../context/AuthContext';
import {
  clientsAPI,
  projectsAPI,
  tasksAPI,
  tagsAPI,
  timeEntriesAPI,
  timeClockAPI,
  organizationsAPI,
  Client,
  Project,
  Task,
  Tag,
  TimeEntry,
} from '../../services/api';

// Tags shown without unfolding the chip list (most-used first).
const VISIBLE_TAG_COUNT = 3;

interface StartTimerDropdownProps {
  onClose: () => void;
}

const StartTimerDropdown: React.FC<StartTimerDropdownProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { startTimer, isLoading, activeTimer, refreshTimer } = useTimer();
  const { user } = useAuth();

  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<
    number | 'internal' | ''
  >('');
  const [selectedProjectId, setSelectedProjectId] = useState<number | ''>('');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedTaskId, setSelectedTaskId] = useState<number | ''>('');
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [tagMode, setTagMode] = useState<'single' | 'multiple'>('multiple');
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [editingEntryId, setEditingEntryId] = useState<number | null>(null);
  const [editDescription, setEditDescription] = useState('');
  const [editHours, setEditHours] = useState('');
  const [editTagIds, setEditTagIds] = useState<number[]>([]);
  const [savingEntry, setSavingEntry] = useState(false);
  const [entryTagFilter, setEntryTagFilter] = useState<number | ''>('');
  const [entryInvoicedFilter, setEntryInvoicedFilter] = useState<
    'all' | 'invoiced' | 'uninvoiced'
  >('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Clockify-style: the dropdowns live in a collapsible details box.
  const [detailsOpen, setDetailsOpen] = useState(!activeTimer);

  // Seed the dropdowns from the running timer so edits apply to it.
  useEffect(() => {
    if (!activeTimer) return;
    setSelectedProjectId(activeTimer.project?.id ?? '');
    setSelectedTagIds(activeTimer.tag_ids || []);
  }, [activeTimer?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reflect the running timer's task once its project's tasks are loaded.
  useEffect(() => {
    if (!activeTimer?.task) return;
    if (tasks.some((task) => task.id === activeTimer.task?.id)) {
      setSelectedTaskId(activeTimer.task.id);
    }
  }, [tasks, activeTimer?.task]); // eslint-disable-line react-hooks/exhaustive-deps

  // Push a change onto the running timer and refresh the shared context.
  const applyTimerUpdate = async (data: {
    project_id?: number | null;
    task_id?: number | null;
    tag_ids?: number[];
  }) => {
    if (!activeTimer) return;
    try {
      await timeClockAPI.update(activeTimer.id, data);
      await refreshTimer();
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t('timer.timerUpdateFailed', {
            defaultValue: 'Failed to update the timer.',
          }),
      );
    }
  };

  // Fetch clients, projects and tags on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [clientsRes, projectsRes, tagsRes] = await Promise.all([
          clientsAPI.getAll(),
          projectsAPI.getAll(),
          tagsAPI.getAll(),
        ]);
        setClients(clientsRes.data.clients || []);
        const projectsList = projectsRes.data.projects || [];
        setProjects(projectsList);
        setFilteredProjects(projectsList);
        setTags(tagsRes.data || []);
      } catch (err: any) {
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Organization policy: may employees pick one tag or several on the timer?
  useEffect(() => {
    if (!user?.organization?.id) return;
    organizationsAPI
      .get(user.organization.id)
      .then((r) =>
        setTagMode(r.data.organization.timer_tag_selection || 'multiple'),
      )
      .catch(() => {});
  }, [user?.organization?.id]);

  // Load my time entries for the in-popup list (scoped to the current user).
  useEffect(() => {
    if (!user?.id) return;
    timeEntriesAPI
      .getAll({ user_id: user.id })
      .then((r) => setEntries(r.data.time_entries || []))
      .catch(() => setEntries([]));
  }, [user?.id]);

  // Load the selected project's tasks; reset the task choice when it changes.
  useEffect(() => {
    setSelectedTaskId('');
    if (!selectedProjectId) {
      setTasks([]);
      return;
    }
    let cancelled = false;
    tasksAPI
      .getAll({ project_id: selectedProjectId })
      .then((res) => {
        if (!cancelled) setTasks(res.data.tasks || []);
      })
      .catch(() => {
        if (!cancelled) setTasks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId]);

  // Filter projects when client selection changes
  useEffect(() => {
    if (selectedClientId === '') {
      setFilteredProjects(projects);
    } else if (selectedClientId === 'internal') {
      setFilteredProjects(projects.filter((p) => p.is_internal));
    } else {
      setFilteredProjects(
        projects.filter((p) => p.client_id === selectedClientId),
      );
    }
    setSelectedProjectId('');
  }, [selectedClientId, projects]);

  // Clockify-style: Start fires immediately; project/task/tags are optional
  // and can be set (or changed) on the running timer from the details box.
  const handleStart = async () => {
    setError(null);
    try {
      await startTimer(
        selectedProjectId ? (selectedProjectId as number) : undefined,
        selectedTaskId ? (selectedTaskId as number) : undefined,
        selectedTagIds,
      );
      setDetailsOpen(true);
    } catch (err: any) {
      setError(err.message || 'Failed to start timer');
    }
  };

  const toggleTag = (id: number) => {
    const next = selectedTagIds.includes(id)
      ? selectedTagIds.filter((x) => x !== id)
      : tagMode === 'single'
        ? [id]
        : [...selectedTagIds, id];
    setSelectedTagIds(next);
    if (activeTimer) void applyTimerUpdate({ tag_ids: next });
  };

  const toggleEditTag = (id: number) => {
    setEditTagIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return tagMode === 'single' ? [id] : [...prev, id];
    });
  };

  // Most-used tags first, computed from my own entries.
  const sortedTags = useMemo(() => {
    const counts = new Map<number, number>();
    entries.forEach((e) =>
      (e.tag_ids || []).forEach((id) =>
        counts.set(id, (counts.get(id) || 0) + 1),
      ),
    );
    return [...tags].sort(
      (a, b) => (counts.get(b.id) || 0) - (counts.get(a.id) || 0),
    );
  }, [tags, entries]);

  // Collapsed: the most-used tags plus any already-selected ones.
  const visibleTags = tagsExpanded
    ? sortedTags
    : sortedTags.filter(
        (tag, i) => i < VISIBLE_TAG_COUNT || selectedTagIds.includes(tag.id),
      );
  const hiddenTagCount = sortedTags.length - visibleTags.length;

  const startEditingEntry = (entry: TimeEntry) => {
    setEditingEntryId(entry.id ?? null);
    setEditDescription(entry.description || '');
    setEditHours(String(entry.hours_worked ?? ''));
    setEditTagIds(entry.tag_ids || []);
  };

  const saveEntry = async (id: number) => {
    setSavingEntry(true);
    setError(null);
    try {
      await timeEntriesAPI.update(id, {
        description: editDescription,
        hours_worked: Number(editHours),
        tag_ids: editTagIds,
      } as Partial<TimeEntry>);
      if (user?.id) {
        const r = await timeEntriesAPI.getAll({ user_id: user.id });
        setEntries(r.data.time_entries || []);
      }
      setEditingEntryId(null);
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t('timer.entryUpdateFailed', {
            defaultValue: 'Failed to update the entry.',
          }),
      );
    } finally {
      setSavingEntry(false);
    }
  };

  const filteredEntries = entries.filter((e) => {
    if (entryTagFilter !== '' && !(e.tag_ids || []).includes(entryTagFilter)) {
      return false;
    }
    if (entryInvoicedFilter === 'invoiced' && !e.invoiced) return false;
    if (entryInvoicedFilter === 'uninvoiced' && e.invoiced) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="absolute top-full right-0 mt-2 w-80 bg-slate-900 rounded-lg shadow-xl border border-slate-700 p-4 z-50">
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute top-full right-0 mt-2 w-80 bg-slate-900 rounded-lg shadow-xl border border-slate-700 z-50">
      {/* Header — what the timer is running on, or a prompt to start one */}
      <div className="px-4 py-3 border-b border-slate-700">
        <h3 className="text-sm font-medium text-white truncate">
          {activeTimer
            ? `⏱ ${activeTimer.project?.name || t('timer.noProject', { defaultValue: 'No project' })}${
                activeTimer.task?.title ? ` · ${activeTimer.task.title}` : ''
              }`
            : t('timer.whatAreYouWorkingOn', {
                defaultValue: 'What are you working on?',
              })}
        </h3>
      </div>

      {/* Form */}
      <div className="p-4 space-y-4">
        {error && (
          <div className="text-sm text-red-400 bg-red-500/10 px-3 py-2 rounded-lg">
            {error}
          </div>
        )}

        {/* Start now (Clockify-style) — details can be filled while it runs */}
        {!activeTimer && (
          <button
            onClick={handleStart}
            disabled={isLoading}
            className="w-full px-4 py-2.5 text-sm font-semibold bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 disabled:opacity-50 transition-all duration-200 flex items-center justify-center"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            ) : (
              <>
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {t('timer.start')}
              </>
            )}
          </button>
        )}

        {/* Details box toggle — project, task and tags */}
        <button
          type="button"
          onClick={() => setDetailsOpen((v) => !v)}
          className="w-full flex items-center justify-between text-xs font-medium text-slate-400 hover:text-white transition-colors"
        >
          <span>
            {t('timer.details', { defaultValue: 'Details' })}
            {activeTimer &&
              ` — ${t('timer.detailsApplyLive', { defaultValue: 'changes apply to the running timer' })}`}
          </span>
          <svg
            className={`w-4 h-4 transition-transform ${detailsOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {detailsOpen && (
          <>
        {/* Client Selection */}
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">
            {t('timeEntry.client')}
          </label>
          <select
            value={selectedClientId}
            onChange={(e) => {
              const val = e.target.value;
              setSelectedClientId(
                val === 'internal' ? 'internal' : val ? Number(val) : '',
              );
            }}
            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">{t('timer.allClients')}</option>
            <option value="internal">{t('timer.internalProjects')}</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </div>

        {/* Project Selection */}
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">
            {t('timeEntry.project')} *
          </label>
          <select
            value={selectedProjectId}
            onChange={(e) => {
              const value = e.target.value ? Number(e.target.value) : '';
              setSelectedProjectId(value);
              if (activeTimer) {
                void applyTimerUpdate({ project_id: value || null });
              }
            }}
            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">{t('timer.selectAProject')}</option>
            {filteredProjects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
                {project.client?.name && ` (${project.client.name})`}
                {project.is_internal && ` (${t('timer.internal')})`}
              </option>
            ))}
          </select>
        </div>

        {/* Task Selection — always visible; enabled once a project with tasks is chosen (then required) */}
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">
            {t('timeEntry.task', { defaultValue: 'Task' })}
            {tasks.length > 0 && ' *'}
          </label>
          <select
            value={selectedTaskId}
            onChange={(e) => {
              const value = e.target.value ? Number(e.target.value) : '';
              setSelectedTaskId(value);
              if (activeTimer) {
                void applyTimerUpdate({ task_id: value || null });
              }
            }}
            disabled={!selectedProjectId || tasks.length === 0}
            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">
              {!selectedProjectId
                ? t('timer.selectProjectFirst', {
                    defaultValue: 'Select a project first...',
                  })
                : tasks.length === 0
                  ? t('timer.noTasks', {
                      defaultValue: 'No tasks in this project',
                    })
                  : t('timer.selectATask', {
                      defaultValue: 'Select a task...',
                    })}
            </option>
            {tasks.map((task) => (
              <option key={task.id} value={task.id}>
                {task.title}
              </option>
            ))}
          </select>
        </div>

        {/* Tags — most-used first, collapsed to one row, single/multiple per org policy */}
        {tags.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">
              {t('timeEntry.tags', { defaultValue: 'Tags' })}
              {tagMode === 'single' && (
                <span className="ml-1 text-slate-500">
                  {t('timer.oneTagOnly', { defaultValue: '(one only)' })}
                </span>
              )}
            </label>
            <div className="flex flex-wrap gap-2">
              {visibleTags.map((tag) => {
                const selected = selectedTagIds.includes(tag.id);
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleTag(tag.id)}
                    className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                      selected
                        ? 'bg-blue-500/20 border-blue-400 text-blue-200'
                        : 'bg-slate-700 border-slate-600 text-slate-300 hover:border-slate-400'
                    }`}
                  >
                    {tag.name}
                  </button>
                );
              })}
              {(hiddenTagCount > 0 || tagsExpanded) && (
                <button
                  type="button"
                  onClick={() => setTagsExpanded((v) => !v)}
                  className="px-2 py-1 text-xs rounded-full border border-dashed border-slate-500 text-slate-400 hover:text-white hover:border-slate-300 transition-colors"
                >
                  {tagsExpanded
                    ? t('timer.lessTags', { defaultValue: 'Less' })
                    : `+${hiddenTagCount}`}
                </button>
              )}
            </div>
          </div>
        )}

          </>
        )}

        {/* My time entries — scrollable list with tag + invoiced filters */}
        <div className="pt-3 border-t border-slate-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-slate-400">
              {t('timer.myEntries', { defaultValue: 'My time entries' })}
            </span>
          </div>

          <div className="flex gap-2 mb-2">
            <select
              value={entryTagFilter}
              onChange={(e) =>
                setEntryTagFilter(e.target.value ? Number(e.target.value) : '')
              }
              className="flex-1 min-w-0 px-2 py-1 bg-slate-700 border border-slate-600 rounded-lg text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">
                {t('timer.allTags', { defaultValue: 'All tags' })}
              </option>
              {tags.map((tag) => (
                <option key={tag.id} value={tag.id}>
                  {tag.name}
                </option>
              ))}
            </select>
            <select
              value={entryInvoicedFilter}
              onChange={(e) =>
                setEntryInvoicedFilter(
                  e.target.value as 'all' | 'invoiced' | 'uninvoiced',
                )
              }
              className="flex-1 min-w-0 px-2 py-1 bg-slate-700 border border-slate-600 rounded-lg text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="all">
                {t('timer.invoicedAll', { defaultValue: 'All' })}
              </option>
              <option value="invoiced">
                {t('timer.invoicedOnly', { defaultValue: 'Invoiced' })}
              </option>
              <option value="uninvoiced">
                {t('timer.uninvoicedOnly', { defaultValue: 'Not invoiced' })}
              </option>
            </select>
          </div>

          <div
            className={`${
              editingEntryId || !detailsOpen ? 'max-h-96' : 'max-h-44'
            } overflow-y-auto space-y-1.5 pr-1`}
          >
            {filteredEntries.length === 0 ? (
              <p className="text-xs text-slate-500 py-2 text-center">
                {t('timer.noEntries', { defaultValue: 'No time entries.' })}
              </p>
            ) : (
              filteredEntries.map((e) =>
                editingEntryId === e.id ? (
                  /* Inline quick-edit: description, hours, tags */
                  <div
                    key={e.id}
                    className="p-2 rounded-lg bg-slate-800 border border-blue-500/40 space-y-2"
                  >
                    <div className="text-xs text-slate-400 truncate">
                      {e.project?.name || '—'}
                      {e.task?.title ? ` · ${e.task.title}` : ''} ·{' '}
                      {e.entry_date}
                    </div>
                    <textarea
                      value={editDescription}
                      onChange={(ev) => setEditDescription(ev.target.value)}
                      rows={2}
                      className="w-full px-2 py-1 bg-slate-700 border border-slate-600 rounded text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <input
                      type="number"
                      step="0.25"
                      min="0"
                      max="24"
                      value={editHours}
                      onChange={(ev) => setEditHours(ev.target.value)}
                      className="w-20 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    {sortedTags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {sortedTags.map((tg) => {
                          const sel = editTagIds.includes(tg.id);
                          return (
                            <button
                              key={tg.id}
                              type="button"
                              onClick={() => toggleEditTag(tg.id)}
                              className={`px-1.5 py-0.5 text-[10px] rounded-full border ${
                                sel
                                  ? 'bg-blue-500/20 border-blue-400 text-blue-200'
                                  : 'bg-slate-700 border-slate-600 text-slate-300'
                              }`}
                            >
                              {tg.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingEntryId(null)}
                        className="px-2 py-1 text-[11px] text-slate-400 hover:text-white"
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        type="button"
                        disabled={savingEntry}
                        onClick={() => e.id && saveEntry(e.id)}
                        className="px-2 py-1 text-[11px] rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        {savingEntry
                          ? t('common.loading')
                          : t('common.save', { defaultValue: 'Save' })}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    key={e.id}
                    className="flex items-start justify-between gap-2 p-2 rounded-lg bg-slate-800/60 border border-slate-700/60"
                  >
                    <div className="min-w-0">
                      <div className="text-xs text-slate-200 truncate">
                        {e.project?.name || '—'}
                        {e.task?.title ? ` · ${e.task.title}` : ''}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {e.entry_date} · {e.hours_worked}h
                      </div>
                      {e.tags && e.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {e.tags.map((tg) => (
                            <span
                              key={tg.id}
                              className="px-1.5 py-0.5 text-[10px] rounded-full bg-slate-700 text-slate-300"
                            >
                              {tg.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
    <div className="flex-shrink-0 flex items-center gap-1">
                      {e.approval_status === 'approved' && (
                        <span
                          title={t('timer.approvedLocked', {
                            defaultValue: 'Approved entries cannot be edited',
                          })}
                          className="px-1.5 py-0.5 text-[10px] rounded-full bg-blue-500/20 text-blue-300"
                        >
                          {t('timer.approvedBadge', {
                            defaultValue: 'Approved',
                          })}
                        </span>
                      )}
                      {e.invoiced && (
                        <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-emerald-500/20 text-emerald-300">
                          {t('timer.invoicedBadge', {
                            defaultValue: 'Invoiced',
                          })}
                        </span>
                      )}
                      {/* Approved entries are locked server-side — no pencil */}
                      {e.approval_status !== 'approved' && !e.invoiced && (
                        <button
                          type="button"
                          onClick={() => startEditingEntry(e)}
                          title={t('common.edit', { defaultValue: 'Edit' })}
                          className="p-1 text-slate-500 hover:text-white transition-colors"
                        >
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                ),
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartTimerDropdown;
